import { noop } from '@openx/utils/noop';
import { createContext, type PropsWithChildren, useContext, useMemo, useReducer } from 'react';

export type ContextData = {
  publisherId: string;
  publisherName: string;
  callback?: () => void;
};

export type RequestModalContext = {
  isOpen: boolean;
  openModal: (data: ContextData) => void;
  closeModal: () => void;
  data: ContextData;
};

const requestModalContextInitialState: RequestModalContext = {
  isOpen: false,
  openModal: noop,
  closeModal: noop,
  data: { publisherId: '', publisherName: '', callback: undefined },
};

type Action = { type: 'OPEN_MODAL'; payload: ContextData } | { type: 'CLOSE_MODAL' };

const reducer = (state: RequestModalContext, action: Action): RequestModalContext => {
  switch (action.type) {
    case 'OPEN_MODAL':
      return { ...state, isOpen: true, data: action.payload };
    case 'CLOSE_MODAL':
      return { ...state, isOpen: false, data: { publisherId: '', publisherName: '', callback: undefined } };
    default:
      return state;
  }
};

const RequestDirectDealModalContext = createContext<RequestModalContext>(requestModalContextInitialState);

export const RequestDirectDealModalProvider = ({ children }: PropsWithChildren) => {
  const [state, dispatch] = useReducer(reducer, requestModalContextInitialState);

  const value: RequestModalContext = useMemo(
    () => ({
      isOpen: state.isOpen,
      openModal: data => dispatch({ type: 'OPEN_MODAL', payload: data }),
      closeModal: () => dispatch({ type: 'CLOSE_MODAL' }),
      data: state.data,
    }),
    [state]
  );

  return <RequestDirectDealModalContext.Provider value={value}>{children}</RequestDirectDealModalContext.Provider>;
};

export const useRequestDirectDealModalContext = () => {
  const context = useContext(RequestDirectDealModalContext);

  if (!context) {
    throw new Error('useRequestDirectDealModalContext must be used within a RequestDirectDealModalProvider');
  }
  return context;
};
